import type { FC } from 'react';

import {
  Box,
  Divider,
  DynamicYield,
  Skeleton,
  Stack,
  useDeviceType,
  ErrorBoundary,
} from '@packages/shared';
import type { CmsData, CmsDataJsonApi } from '@packages/cms-components/interfaces';
import { LazyRender } from '@packages/modules';
import { Banner, ContentExternal } from '@packages/cms-components';
import { useConfig } from '@packages/utilities';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { CategoryPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryPageDefaultContentFragmentFragmentDoc';

import type { SearchSeoApiResponse } from '../../api/rest/types';
import { ProductListPageLayout } from '../ProductListPageLayout';
import { ProductListBreadcrumbs } from '../ProductListBreadcrumbs';
import { ProductListSeo } from '../ProductListSeo';
import { ProductCategoryNavigationDesktop } from '../ProductCategoryNavigationDesktop';
import { SeoProductCategoryNavigation } from '../SeoProductCategoryNavigation';
import { CategoryFilterNavigationMobile } from '../CategoryFilterNavigationMobile';

/* GraphQL */ `
  fragment CategoryPageDefaultContentFragment on CategoryResult {
    ...ProductListPageLayoutFragment
    ...ProductListBreadcrumbsFragment
    ...CategoryFilterNavigationMobileWorkaroundFragment

    seoCategoryNavigation {
      ...SeoProductCategoryNavigationFragment
    }

    CategoryResultCategoryItems: categories {
      ...ProductCategoryNavigationDesktopFragment
      ...CategoryFilterNavigationMobileFragment
    }
    cmsContentPosition
  }
`;

export type CategoryPageDefaultContentProps = {
  maskedData: FragmentType<typeof CategoryPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  bannerData?: CmsDataJsonApi;
  cmsContent?: CmsData;
  seoResponse?: SearchSeoApiResponse;
  referrer?: string;
};

/**
 * CategoryPageDefaultContent component description displayed in storybook
 * */
export const CategoryPageDefaultContent: FC<CategoryPageDefaultContentProps> = ({
  maskedData,
  topLevelCategories,
  bannerData,
  cmsContent,
  seoResponse,
  referrer,
}) => {
  const { isDesktop } = useDeviceType();
  const { dynamicYieldEnabled } = useConfig();

  const data = getFragmentData(CategoryPageDefaultContentFragmentFragmentDoc, maskedData);

  // TODO root (really, more like parent? L1 category?) from GraphQL

  const { cmsContentPosition } = data;

  const seoInformationExists = seoResponse && Object.keys(seoResponse).length > 0;

  return (
    <ProductListPageLayout
      maskedData={data}
      topLevelCategories={topLevelCategories}
      referrer={referrer}
      aboveMainContentComponent={
        <>
          <ProductListBreadcrumbs maskedData={data} />
          {bannerData && cmsContentPosition !== 'TOP' && (
            <Box
              sx={{
                img: {
                  maxWidth: '100%',
                },
              }}
            >
              {dynamicYieldEnabled && (
                <>
                  <DynamicYield
                    dataDyId={`mba_topbanner-services-${isDesktop ? 'desktop' : 'mobile'}`}
                  />
                  <DynamicYield
                    dataDyId={`mba_campaign-banner-${isDesktop ? 'desktop' : 'mobile'}`}
                  />
                </>
              )}
              <ErrorBoundary>
                <Banner bannerData={bannerData} />
              </ErrorBoundary>
            </Box>
          )}
          {cmsContent && cmsContentPosition === 'TOP' && (
            <LazyRender fallback={<Skeleton height={200} />}>
              <ErrorBoundary>
                <ContentExternal data={cmsContent} />
              </ErrorBoundary>
            </LazyRender>
          )}
        </>
      }
      belowProductsComponent={
        <>
          {cmsContent && cmsContentPosition === 'BOTTOM' && <ContentExternal data={cmsContent} />}
          {seoInformationExists && <ProductListSeo data={seoResponse} />}
        </>
      }
      leftSidebarDesktopComponent={
        <Stack spacing={1}>
          <ProductCategoryNavigationDesktop maskedItems={data.CategoryResultCategoryItems} />
          {data.seoCategoryNavigation && (
            <>
              <Divider />
              <SeoProductCategoryNavigation maskedNavigation={data.seoCategoryNavigation} />
            </>
          )}
        </Stack>
      }
      renderCategoryDialogContent={(closeDialog) => (
        <CategoryFilterNavigationMobile
          maskedItems={data.CategoryResultCategoryItems}
          maskedResult={data}
          onNavigate={closeDialog}
        />
      )}
    />
  );
};
